import { fetchCurrentUser } from '../../ducks/user.duck';
import { RESPONSE } from '../../util/enums';
import { OWN, TUTULIST, TUTULIST_LOWERCASE } from '../../util/constants';
import { checkIfEnabledNonUSOrders, getOwnShippingPreference } from '../../util/userDataExtractor';
import { updateProfileThunk } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import {
  buildOwnShippingProfileResetPayload,
  buildTutulistShippingProfilePayload,
} from './shippingDetails.helpers';

const shippingDefaultDimensions = {
  length: '8.5',
  width: '11',
  height: '1',
};

export const getSaveWeightListingUpdateParams = params => {
  const isOwn = params?.isOwn;

  return isOwn
    ? {
        shippingPriceInSubunitsOneItem: params?.shippingPriceInSubunitsOneItem?.amount,
        shippingPriceInSubunitsAdditionalItems:
          params?.shippingPriceInSubunitsAdditionalItems?.amount,
        deliveryOptions: params?.deliveryOptions,
        shippingPreference: OWN,
      }
    : {
        ...shippingDefaultDimensions,
        shippingPreference: TUTULIST,
        shippingPriceInSubunitsOneItem: null,
        shippingPriceInSubunitsAdditionalItems: null,
        deliveryOptions: ['shipping'],
        weight: params?.weight,
        massUnit: params?.massUnit,
      };
};

export const shouldResetCurrentUserShippoState = ({ currentUser, nextShippingPreference }) => {
  return (
    nextShippingPreference === OWN && getOwnShippingPreference(currentUser) === TUTULIST_LOWERCASE
  );
};

export const shouldSaveCurrentUserTutulistPreference = ({
  currentUser,
  nextShippingPreference,
}) => {
  return (
    nextShippingPreference === TUTULIST_LOWERCASE &&
    getOwnShippingPreference(currentUser) !== TUTULIST_LOWERCASE
  );
};

export const getSaveWeightCurrentUserPayload = ({
  currentUser,
  nextShippingPreference,
  params,
}) => {
  if (shouldResetCurrentUserShippoState({ currentUser, nextShippingPreference })) {
    return buildOwnShippingProfileResetPayload({
      currentUser,
      phoneNumber: currentUser?.attributes?.profile?.protectedData?.phoneNumber,
      enableNonUSOrders: checkIfEnabledNonUSOrders(currentUser),
    });
  }

  if (shouldSaveCurrentUserTutulistPreference({ currentUser, nextShippingPreference })) {
    // The preference switch modal only confirms platform shipping defaults.
    // Full Shippo address creation still runs through createShippoAddress when the address form is saved.
    return buildTutulistShippingProfilePayload({
      currentUser,
      weight: params?.weight,
      massUnit: params?.massUnit,
    });
  }

  return null;
};

// ================ Action types ================ //

const SAVE_WEIGHT_REQUEST = 'app/ShippingDetailsPage/SAVE_WEIGHT_REQUEST';
const SAVE_WEIGHT_SUCCESS = 'app/ShippingDetailsPage/SAVE_WEIGHT_SUCCESS';
const SAVE_WEIGHT_ERROR = 'app/ShippingDetailsPage/SAVE_WEIGHT_ERROR';

const CLEAR_FORM = 'app/ShippingDetailsPage/CLEAR_FORM';

// ================ Reducer ================ //

const initialState = {
  saveWeightSuccess: false,
  saveWeightInProgress: false,
  saveWeightError: null,

  clearForm: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case SAVE_WEIGHT_REQUEST:
      return { ...state, saveWeightInProgress: true, saveWeightError: null };
    case SAVE_WEIGHT_SUCCESS:
      return { ...state, saveWeightInProgress: false, saveWeightSuccess: true };
    case SAVE_WEIGHT_ERROR:
      return { ...state, saveWeightInProgress: false, saveWeightError: payload };

    case CLEAR_FORM:
      return { ...state, clearForm: true };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const selectSaveWeightSuccess = state => state.ShippingDetailsPage.saveWeightSuccess;

export const selectSaveWeightInProgress = state => state.ShippingDetailsPage.saveWeightInProgress;

export const selectSaveWeightError = state => state.ShippingDetailsPage.saveWeightError;

// ================ Action creators ================ //

export const saveWeightRequest = () => ({ type: SAVE_WEIGHT_REQUEST });
export const saveWeightSuccess = () => ({ type: SAVE_WEIGHT_SUCCESS });
export const saveWeightError = error => ({ type: SAVE_WEIGHT_ERROR, payload: error });

export const clearForm = () => ({ type: CLEAR_FORM });

// ================ Thunks ================ //

export const saveWeightToAllListings = params => async (dispatch, getState, sdk) => {
  dispatch(saveWeightRequest());
  const currentUser = getState()?.user?.currentUser;
  const nextShippingPreference =
    params?.shippingPreference || (params?.isOwn ? OWN : TUTULIST_LOWERCASE);

  try {
    const response = await sdk.ownListings.query({});
    const listings = response?.data?.data || [];
    const listingUpdateParams = getSaveWeightListingUpdateParams(params);
    const listingUpdates = listings.map(listing =>
      sdk.ownListings.update({
        id: listing?.id?.uuid,
        publicData: { ...listingUpdateParams },
      })
    );
    const currentUserPayload = getSaveWeightCurrentUserPayload({
      currentUser,
      nextShippingPreference,
      params,
    });
    const currentUserUpdatePromise = currentUserPayload
      ? dispatch(updateProfileThunk(currentUserPayload)).unwrap()
      : null;

    // Shipping-mode confirmation can touch both listing defaults and the seller profile.
    // Run them together so success is only reported once every required write finishes.
    await Promise.all([
      ...(currentUserUpdatePromise ? [currentUserUpdatePromise] : []),
      ...listingUpdates,
    ]);

    dispatch(saveWeightSuccess());
    return RESPONSE.SUCCESS;
  } catch (error) {
    dispatch(saveWeightError(error));
    return RESPONSE.ERROR;
  } finally {
    dispatch(clearForm());
  }
};

export const loadData = () => fetchCurrentUser();
